<template>
  <b-container class="mt-2">
    <vue-headful :title="this.setPageTitle('indicatorsHistory', decodedProductName)" />
    <b-row no-gutters>
      <b-col cols="12" class="detailsTemplateProductName mb-3">
        <span class="mr-2">
          <a
            @click.prevent="
              $router.push({
                name: 'productDetails',
                params: {
                  productName: productName,
                  issuer: issuer,
                  referenceDate: referenceDate,
                },
              })
            "
            class="pointer"
          >
            <span class="small mr-2 align-baseline"><i class="fas fa-chevron-left"></i></span></a
        ></span>
        <span>
          <span class="font-weight-bold">{{ decodedProductName }}</span
          ><br />
          <span class="detailsTemplateIssuer">{{ issuerName }}</span>
        </span>
      </b-col>
    </b-row>

    <!-- One row: the columns that are drawn flow two by two, so a hidden category leaves no gap. -->
    <b-row>
      <b-col v-if="indicesValues.riskInd.length > 0" cols="6">
        <b-row class="text-middle mb-4">
          <b-col>
            <div class="chartHeader" v-t="'common.indicators.riskInd'"></div>
            <b-select :options="indicesValues.riskInd" v-model="indicators.riskInd" class="indicator-select"></b-select>
          </b-col>
        </b-row>
        <template v-if="showChart.risk">
          <b-row class="text-middle mb-4">
            <b-col>
              <loading :active="isLoading.risk" :is-full-page="false" :color="loaderColor" loader="spinner"> </loading>
              <highcharts :key="hcKey" :options="chartOptions.riskChart"></highcharts>
            </b-col>
          </b-row>
        </template>
        <b-row v-else class="my-5">
          <b-col class="text-center font-weight-bold"> N/A </b-col>
        </b-row>
      </b-col>
      <b-col v-if="indicesValues.performanceInd.length > 0" cols="6">
        <b-container>
          <b-row class="text-middle mb-4">
            <b-col>
              <div class="chartHeader" v-t="'common.indicators.performanceInd'"></div>
              <b-select
                :options="indicesValues.performanceInd"
                v-model="indicators.performanceInd"
                class="indicator-select"
              ></b-select>
            </b-col>
          </b-row>
          <template v-if="showChart.perf">
            <b-row class="text-middle mb-4">
              <b-col>
                <loading :active="isLoading.perf" :is-full-page="false" :color="loaderColor" loader="spinner">
                </loading>
                <highcharts :key="hcKey" :options="chartOptions.perfChart"></highcharts>
              </b-col>
            </b-row>
          </template>
          <b-row v-else class="my-5">
            <b-col class="text-center font-weight-bold"> N/A </b-col>
          </b-row>
        </b-container>
      </b-col>
      <b-col v-if="indicesValues.returnInd.length > 0" cols="6">
        <b-container>
          <b-row class="text-middle mb-4">
            <b-col>
              <div class="chartHeader" v-t="'common.indicators.returnInd'"></div>
              <b-select
                :options="indicesValues.returnInd"
                v-model="indicators.returnInd"
                class="indicator-select"
              ></b-select>
            </b-col>
          </b-row>
          <template v-if="showChart.return">
            <b-row class="text-middle mb-4">
              <b-col>
                <loading :active="isLoading.return" :is-full-page="false" :color="loaderColor" loader="spinner">
                </loading>
                <highcharts :key="hcKey" :options="chartOptions.returnChart"></highcharts>
              </b-col>
            </b-row>
          </template>
          <b-row v-else class="my-5">
            <b-col class="text-center font-weight-bold"> N/A </b-col>
          </b-row>
        </b-container>
      </b-col>
      <b-col v-if="indicesValues.costInd.length > 0" cols="6">
        <b-container>
          <b-row class="text-middle mb-4">
            <b-col>
              <div class="chartHeader" v-t="'common.indicators.costInd'"></div>
              <b-select
                :options="indicesValues.costInd"
                v-model="indicators.costInd"
                class="indicator-select"
              ></b-select>
            </b-col>
          </b-row>
          <template v-if="showChart.cost">
            <b-row class="text-middle mb-4">
              <b-col>
                <loading :active="isLoading.cost" :is-full-page="false" :color="loaderColor" loader="spinner">
                </loading>
                <highcharts :key="hcKey" :options="chartOptions.costChart"></highcharts>
              </b-col>
            </b-row>
          </template>
          <b-row v-else class="my-5">
            <b-col class="text-center font-weight-bold"> N/A </b-col>
          </b-row>
        </b-container>
      </b-col>
      <b-col v-if="indicesValues.otherInd.length > 0" cols="6">
        <b-container>
          <b-row class="text-middle mb-4">
            <b-col>
              <div class="chartHeader" v-t="'common.indicators.otherInd'"></div>
              <b-select
                :options="indicesValues.otherInd"
                v-model="indicators.otherInd"
                class="indicator-select"
              ></b-select>
            </b-col>
          </b-row>
          <template v-if="showChart.other">
            <b-row class="text-middle mb-4">
              <b-col>
                <loading :active="isLoading.other" :is-full-page="false" :color="loaderColor" loader="spinner">
                </loading>
                <highcharts :key="hcKey" :options="chartOptions.otherChart"></highcharts>
              </b-col>
            </b-row>
          </template>
          <b-row v-else class="my-5">
            <b-col class="text-center font-weight-bold"> N/A </b-col>
          </b-row>
        </b-container>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { rhpToYears, round, setChartMenuItemsLang } from '../utils/formatter'
import { base64UrlDecode, isBase64Url } from '../utils/sanitizer'
import { isValidDate } from '../utils/date'
import { assetClassStorageKey } from '../utils/assetClassState'
import validIndicatorCodes from '../mixins/validIndicatorCodes'

export default {
  name: 'indicatorsHistory',
  mixins: [validIndicatorCodes],
  props: { productName: {}, issuer: {}, referenceDate: {} },
  data() {
    return {
      hcKey: 0,
      isLoading: {
        risk: false,
        perf: false,
        cost: false,
        return: false,
        other: false,
      },
      indicators: {
        riskInd: 'MRM',
        performanceInd: 'SharpeRatioOnRiskFree',
        returnInd: 'StressReturn',
        costInd: 'EntryCosts',
        otherInd: 'KIDRHP',
      },
      BEResponses: {
        risk: {
          data: { chartItems: [] },
        },
        perf: {
          data: { chartItems: [] },
        },
        return: {
          data: { chartItems: [] },
        },
        cost: {
          data: { chartItems: [] },
        },
        other: {
          data: { chartItems: [] },
        },
      },
      loaderColor: 'blue',
    }
  },
  watch: {
    indicators: {
      deep: true,
      handler(newVal) {
        localStorage.setItem(assetClassStorageKey('okv-indicators-history'), JSON.stringify(newVal))
      },
    },
    'indicators.riskInd': function (newValue) {
      if (this.$_restoring) return
      this.isLoading.risk = true
      let dataToSend = {
        productKey: { issuerCode: this.issuer, name: this.decodedProductName, referenceDate: this.referenceDate },
        indicators: [newValue],
      }

      this.updateChart(dataToSend, this._riskCallback)
    },
    'indicators.performanceInd': function (newValue) {
      if (this.$_restoring) return
      this.isLoading.perf = true
      let dataToSend = {
        productKey: { issuerCode: this.issuer, name: this.decodedProductName, referenceDate: this.referenceDate },
        indicators: [newValue],
      }

      this.updateChart(dataToSend, this._perfCallback)
    },
    'indicators.returnInd': function (newValue) {
      if (this.$_restoring) return
      this.isLoading.return = true
      let dataToSend = {
        productKey: { issuerCode: this.issuer, name: this.decodedProductName, referenceDate: this.referenceDate },
        indicators: [newValue],
      }

      this.updateChart(dataToSend, this._returnCallback)
    },
    'indicators.costInd': function (newValue) {
      if (this.$_restoring) return
      this.isLoading.cost = true
      let dataToSend = {
        productKey: { issuerCode: this.issuer, name: this.decodedProductName, referenceDate: this.referenceDate },
        indicators: [newValue],
      }

      this.updateChart(dataToSend, this._costCallback)
    },
    'indicators.otherInd': function (newValue) {
      if (this.$_restoring) return
      this.isLoading.other = true
      let dataToSend = {
        productKey: { issuerCode: this.issuer, name: this.decodedProductName, referenceDate: this.referenceDate },
        indicators: [newValue],
      }

      this.updateChart(dataToSend, this._otherCallback)
    },
  },
  methods: {
    dateFormatter: function (val) {
      return this.$d(Date.parse(val.value))
    },
    commonChartOptions: function (chartName) {
      return {
        lang: this.setOptions,
        chart: {
          zoomType: 'xy',
          backgroundColor: '#FFFFFF',
          margin: [50, 30, 70, 100],
          height: '450px',
        },
        credits: {
          enabled: false,
        },
        legend: {
          enabled: this.isMop,
          verticalAlign: 'top',
        },
        title: {
          text: null,
        },
        xAxis: {
          labels: {
            formatter: this.dateFormatter,
          },
          title: {},
          gridLineWidth: 2,
          categories: this.BEResponses[chartName].data.chartItems.map(
            (el) => el.productKey.referenceDate.split('T')[0]
          ),
        },
        series: [
          {
            name: 'Min',
            data: this.BEResponses[chartName].data.chartItems.map((el) => round(el.min, 4)),
          },
          {
            name: 'Med',
            data: this.BEResponses[chartName].data.chartItems.map((el) => round(el.med, 4)),
          },
          {
            name: 'Max',
            data: this.BEResponses[chartName].data.chartItems.map((el) => round(el.max, 4)),
          },
        ],
      }
    },
    searchRequest() {
      let dataToSend = {
        productKey: { issuerCode: this.issuer, name: this.decodedProductName, referenceDate: this.referenceDate },
        // Only the indicators of the categories that have something to offer: a category left without any
        // available indicator is not drawn, so there is nothing to ask for.
        indicators: Object.keys(this.indicators)
          .filter((category) => this.indicesValues[category].length > 0)
          .map((category) => this.indicators[category]),
      }
      Object.keys(this.isLoading).forEach((key) => (this.isLoading[key] = true))
      let callbackCollection = (resp) => {
        this._riskCallback(resp)
        this._perfCallback(resp)
        this._returnCallback(resp)
        this._costCallback(resp)
        this._otherCallback(resp)
      }
      this.updateChart(dataToSend, callbackCollection)
    },
    updateChart(dataToSend, callback) {
      this.post('/api/charts/indicatorsHistory', dataToSend)
        .then((resp) => {
          if (resp != null) {
            callback(resp)
          } else {
            alert(this.$t('common.errorReceivingData'))
          }
        })
        .catch((e) => {
          this.httpErrorsHandler(e)
        })
    },
    // The chart data of an indicator, or an empty one when the backend has none for it (an indicator not
    // available for the asset class is left out of the answer): the charts read `chartItems` unconditionally.
    _chartData(resp, indicatorCode) {
      return resp.find((el) => el.indicatorCode === indicatorCode) || { chartItems: [] }
    },
    _riskCallback(resp) {
      this.BEResponses.risk.data = this._chartData(resp, this.indicators.riskInd)
      this.isLoading.risk = false
    },
    _perfCallback(resp) {
      this.BEResponses.perf.data = this._chartData(resp, this.indicators.performanceInd)
      this.isLoading.perf = false
    },
    _returnCallback(resp) {
      this.BEResponses.return.data = this._chartData(resp, this.indicators.returnInd)
      this.isLoading.return = false
    },
    _costCallback(resp) {
      this.BEResponses.cost.data = this._chartData(resp, this.indicators.costInd)
      this.isLoading.cost = false
    },
    _otherCallback(resp) {
      this.BEResponses.other.data = this._chartData(resp, this.indicators.otherInd)
      this.isLoading.other = false
    },
  },
  computed: {
    // structureType/issuerName arrive as query params from ProductDetails.vue's own pushToRoute
    // (already-loaded Characteristics/top-level DetailPageResponse data) — this page no longer fetches
    // its own copy via the deleted `/api/detail/product` (ProductDetailsEndpoint).
    isMop: function () {
      return this.$route.query.structureType === 'Mop'
    },
    issuerName: function () {
      return this.$route.query.issuerName
    },
    chartOptions: function () {
      let vueThis = this
      return {
        riskChart: {
          ...this.commonChartOptions('risk'),
          yAxis: {
            labels: {
              formatter: (val) => (vueThis.isSriOrMrm ? val.value : vueThis.$n(val.value, 'percent')),
            },
            allowDecimals: !this.isSriOrMrm,
            title: {
              text: this.indicesValues.riskInd[this.indicators.riskInd],
            },
            ...(this.isSriOrMrm ? { min: 1, max: 7 } : { min: null, max: null }),
          },
          tooltip: {
            formatter: function () {
              return (
                vueThis.dateFormatter({ value: this.point.category }) +
                '<br>' +
                (vueThis.isSriOrMrm ? this.point.y : vueThis.$n(this.point.y, 'percent'))
              )
            },
          },
        },
        perfChart: {
          ...this.commonChartOptions('perf'),
          yAxis: {
            labels: {
              formatter: (val) => this.$n(val.value, 'decimal'),
            },
            title: {
              text: this.indicesValues.performanceInd[this.indicators.performanceInd],
            },
          },
          tooltip: {
            formatter: function () {
              return (
                vueThis.dateFormatter({ value: this.point.category }) + '<br>' + vueThis.$n(this.point.y, 'decimal')
              )
            },
          },
        },
        returnChart: {
          ...this.commonChartOptions('return'),
          yAxis: {
            labels: {
              formatter: (val) => this.$n(val.value, 'percent'),
            },
            title: {
              text: this.indicesValues.returnInd[this.indicators.returnInd],
            },
          },
          tooltip: {
            formatter: function () {
              return (
                vueThis.dateFormatter({ value: this.point.category }) + '<br>' + vueThis.$n(this.point.y, 'percent')
              )
            },
          },
        },
        costChart: {
          ...this.commonChartOptions('cost'),
          yAxis: {
            labels: {
              formatter: (val) => this.$n(val.value, 'percent'),
            },
            title: {
              text: this.indicesValues.costInd[this.indicators.costInd],
            },
          },
          tooltip: {
            formatter: function () {
              return (
                vueThis.dateFormatter({ value: this.point.category }) + '<br>' + vueThis.$n(this.point.y, 'percent')
              )
            },
          },
        },
        otherChart: {
          ...this.commonChartOptions('other'),
          yAxis: {
            labels: {
              formatter: (val) => rhpToYears(val.value),
            },
            title: {
              text: this.indicesValues.otherInd[this.indicators.otherInd],
            },
          },
          tooltip: {
            formatter: function () {
              return vueThis.dateFormatter({ value: this.point.category }) + '<br>' + rhpToYears(this.point.y)
            },
          },
        },
      }
    },
    showChart: function () {
      const resp = this.BEResponses
      return {
        risk: resp.risk.data != undefined && resp.risk.data.chartItems.length > 0,
        perf: resp.perf.data != undefined && resp.perf.data.chartItems.length > 0,
        return: resp.return.data != undefined && resp.return.data.chartItems.length > 0,
        cost: resp.cost.data != undefined && resp.cost.data.chartItems.length > 0,
        other: resp.other.data != undefined && resp.other.data.chartItems.length > 0,
      }
    },
    isSriOrMrm: function () {
      return ['MRM', 'SRI'].includes(this.indicators.riskInd)
    },
    decodedProductName: function () {
      if (isBase64Url(this.productName)) {
        return base64UrlDecode(this.productName)
      } else {
        return this.productName
      }
    },
    indicesValues: function () {
      const all = this.getMarketComparationIndices
      const available = {}
      Object.keys(all).forEach((category) => {
        available[category] = this.keepAvailableIndicators(all[category])
      })
      return available
    },
    setOptions() {
      this.hcKey += 1
      return setChartMenuItemsLang()
    },
  },
  async mounted() {
    if (this.decodedProductName === undefined) {
      return this.$router.push('/search')
    }

    if (this.referenceDate != null && !isValidDate(this.referenceDate)) {
      this.httpErrorsHandler('404')
      return
    }

    // Awaited before restoring the saved selection, so only indicators available for the asset class are offered.
    await this.loadValidIndicatorCodes()

    const saved = JSON.parse(localStorage.getItem(assetClassStorageKey('okv-indicators-history')))
    if (saved) {
      this.$_restoring = true
      Object.keys(this.indicators).forEach((key) => {
        const isValid = this.indicesValues[key] && this.indicesValues[key].some((o) => o.value === saved[key])
        if (isValid) {
          this.indicators[key] = saved[key]
        }
      })
      this.$_restoring = false
    }

    // A default that is not available for this asset class falls back to the first one that is.
    this.$_restoring = true
    Object.keys(this.indicators).forEach((key) => {
      const options = this.indicesValues[key]
      if (options && options.length > 0 && !options.some((o) => o.value === this.indicators[key])) {
        this.indicators[key] = options[0].value
      }
    })
    this.$_restoring = false

    this.searchRequest()
  },
}
</script>

<style lang="scss" scoped>
.chartHeader {
  font-size: 1.3rem;
  font-weight: bold;
}

.indicator-select {
  max-width: 25rem;
}

.detailsTemplateProductName {
  font-size: 1.3rem;
}

.detailsTemplateProductName span {
  display: inline-block;
  vertical-align: top;
}

.detailsTemplateIssuer {
  font-size: 1rem;
}
</style>
